import React, { useState, useEffect } from "react";
import {
  getDojos,
  deleteDojo,
  updateOrderDojo,
  getGroupsByDojo,
} from "../../../services/dojoServices";
import Button from "../../../components/Button/Button";
import ButtonEdit from "../../../components/Button/ButtonEdit";
import ButtonDelete from "../../../components/Button/ButtonDelete";
import { FaPlus } from "react-icons/fa";
import { TbTriangleFilled, TbTriangleInvertedFilled } from "react-icons/tb";
import { ToastContainer, toast } from "react-toastify";
import CreateDojoModal from "./CreateDojoModal";
import EditDojoModal from "./EditDojoModal";

const SettingsDojos = () => {
  const [dojos, setDojos] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedDojo, setSelectedDojo] = useState(null);

  useEffect(() => {
    fetchDojos();
  }, []);

  const fetchDojos = async () => {
    try {
      const data = await getDojos();
      setDojos(data);
    } catch (error) {
      console.error("Erreur lors de la récupération des dojos", error);
    }
  };

  const handleDeleteDojo = async (dojo) => {
    try {
      // Récupérer les groupes associés au dojo
      const groupes = await getGroupsByDojo(dojo.id);

      if (groupes.length > 0) {
        // Extraire les noms des groupes et les concaténer en une seule chaîne
        const groupeNames = groupes.map((g) => g.nom).join(", ");

        // Afficher un message d'erreur avec la liste des groupes
        toast.error(
          `Impossible de supprimer ${dojo.dojo_nom}, il contient encore ces groupes : ${groupeNames}.`
        );
        return;
      }

      if (
        window.confirm(
          `Êtes-vous sûr de vouloir supprimer le dojo ${dojo.dojo_nom} ?`
        )
      ) {
        await deleteDojo(dojo.id);
        toast.success("Dojo supprimé avec succès");
        fetchDojos();
      }
    } catch (error) {
      toast.error(
        "Erreur lors de la vérification des groupes ou de la suppression du dojo"
      );
      console.error("Erreur lors de la suppression du dojo :", error);
    }
  };

  const openEditModal = (dojo) => {
    setSelectedDojo(dojo);
  };

  const closeEditModal = () => {
    setSelectedDojo(null);
  };

  const moveDojoUp = async (index) => {
    if (index === 0) return; // Premier élément ne peut pas être déplacé vers le haut
    const newDojos = [...dojos];
    const temp = newDojos[index - 1];
    newDojos[index - 1] = newDojos[index];
    newDojos[index] = temp;
    newDojos[index - 1].dojo_ordre--;
    newDojos[index].dojo_ordre++;
    setDojos(newDojos);
    await updateOrderDojo(newDojos);
    fetchDojos();
  };

  const moveDojoDown = async (index) => {
    if (index === dojos.length - 1) return; // Dernier élément ne peut pas être déplacé vers le bas
    const newDojos = [...dojos];
    const temp = newDojos[index + 1];
    newDojos[index + 1] = newDojos[index];
    newDojos[index] = temp;
    newDojos[index + 1].dojo_ordre++;
    newDojos[index].dojo_ordre--;
    setDojos(newDojos);
    await updateOrderDojo(newDojos);
    fetchDojos();
  };

  return (
    <div className="bg-white dark:bg-slate-300 shadow-md rounded-lg p-2 max-w-4xl w-full">
      <div className="flex justify-center mb-4">
        <Button
          text="Créer un Dojo"
          icon={FaPlus}
          onClick={() => setIsCreateModalOpen(true)}
          className="mb-3 gap-2"
          hoverLabel="Créer un nouveau dojo"
        />
      </div>

      {isCreateModalOpen && (
        <CreateDojoModal
          isActive={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          refreshDojos={fetchDojos}
        />
      )}

      {selectedDojo && (
        <EditDojoModal
          isActive={Boolean(selectedDojo)}
          onClose={closeEditModal}
          refreshDojos={fetchDojos}
          dojoData={selectedDojo}
        />
      )}

      <div className="overflow-x-auto table-scrollable">
        <h2 className="text-2xl font-bold mb-4 text-left dark:text-dark">
          Gestion des Dojos
        </h2>
        <table className="table-auto bg-white dark:bg-slate-300 font-montserrat mx-auto border-collapse border-2 border-slate-300">
          <thead className="bg-gray-800 text-white">
            <tr className="border-collapse border-2 border-slate-300">
              <th className="px-3 py-2 border-b-2 border-gray-200 text-center text-xs font-semibold text-gray-200 uppercase tracking-wider">
                Nom
              </th>
              <th className="px-3 py-2 border-b-2 border-gray-200 text-center text-xs font-semibold text-gray-200 uppercase tracking-wider">
                Discipline
              </th>
              <th className="px-3 py-2 border-b-2 border-gray-200 text-center text-xs font-semibold text-gray-200 uppercase tracking-wider">
                Adresse
              </th>
              <th className="px-3 py-2 border-b-2 border-gray-200 text-center text-xs font-semibold text-gray-200 uppercase tracking-wider">
                Ordre
              </th>
              <th className="px-3 py-2 border-b-2 border-gray-200 text-center text-xs font-semibold text-gray-200 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {dojos.map((dojo, index) => (
              <tr key={dojo.id}>
                <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {dojo.dojo_nom}
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {dojo.dojo_discipline}
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                  {dojo.dojo_adresse}
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center">
                  {dojo.dojo_ordre}
                  <div className="flex flex-col ml-2">
                    <button
                      onClick={() => moveDojoUp(index)}
                      className="text-blue-400 hover:text-blue-900"
                    >
                      <TbTriangleFilled />
                    </button>
                    <button
                      onClick={() => moveDojoDown(index)}
                      className="text-blue-400 hover:text-blue-900"
                    >
                      <TbTriangleInvertedFilled />
                    </button>
                  </div>
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex">
                    <ButtonEdit
                      onClick={() => openEditModal(dojo)}
                      className="mr-2"
                    />
                    <ButtonDelete
                      onClick={() => handleDeleteDojo(dojo)}
                      className="ml-2"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ToastContainer />
    </div>
  );
};

export default SettingsDojos;
